import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';

export default function SyncContactsConflicts({
    showConflictModal,
    setShowConflictModal,
    selectedConnection,
    conflicts,
    resolutions,
    setResolutions,
    showAllSyncContacts,
    setShowAllSyncContacts,
    executePullSyncWithResolutions,
    syncing
}) {
    const platformName = selectedConnection ? (selectedConnection.platform === 'xero' ? 'Xero' : selectedConnection.platform === 'quickbooks' ? 'QuickBooks' : 'MYOB') : 'Platform';
    const hasUnselectedTypes = conflicts.some(c => c.isTypeSelectable && !resolutions[c.externalId]?.vendor_type);

    return (
        <Modal show={showConflictModal} onHide={() => setShowConflictModal(false)} size="xl" centered dialogClassName="sync-contacts-modal" data-cy="sync-contacts-conflict-modal">
            <Modal.Header closeButton>
                <Modal.Title>Resolve Pull Conflicts ({platformName})</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-muted mb-4">
                    The following discrepancies were found between Contractable and the accounting platform. Choose which values to keep:
                </p>
                
                <div className="mb-3 d-flex gap-2">
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        data-cy="sync-contacts-conflict-overwrite-all"
                        onClick={() => {
                            const updated = { ...resolutions };
                            conflicts.forEach(contact => {
                                const fieldsRes = { ...updated[contact.externalId] };
                                contact.fields.forEach(f => {
                                    if (f.isConflict) {
                                        if (!f.external && f.isMandatory) {
                                            fieldsRes[f.name] = 'local';
                                        } else {
                                            fieldsRes[f.name] = 'external';
                                        }
                                    }
                                });
                                updated[contact.externalId] = fieldsRes;
                            });
                            setResolutions(updated);
                        }}
                    >
                        Overwrite All Conflicts with External Data
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        size="sm"
                        className="ms-2"
                        data-cy="sync-contacts-conflict-keep-local"
                        onClick={() => {
                            const updated = { ...resolutions };
                            conflicts.forEach(contact => {
                                const fieldsRes = { ...updated[contact.externalId] };
                                contact.fields.forEach(f => {
                                    if (f.isConflict) {
                                        fieldsRes[f.name] = 'local';
                                    }
                                });
                                updated[contact.externalId] = fieldsRes;
                            });
                            setResolutions(updated);
                        }}
                    >
                        Keep All Local Data
                    </Button>
                    <Form.Check 
                        type="switch"
                        id="reusable-show-all-sync-contacts"
                        label="Show all contacts (including ready to pull)"
                        checked={showAllSyncContacts}
                        onChange={(e) => setShowAllSyncContacts(e.target.checked)}
                        className="fw-semibold ms-auto"
                        data-cy="sync-contacts-conflict-show-all-switch"
                    />
                </div>

                <div className="alert alert-info py-2.5 px-3 mb-4 d-flex justify-content-between align-items-center conflict-alert">
                    <div className="text-dark">
                        Showing <strong>{conflicts.length}</strong> contact(s) total
                    </div>
                    <div className="d-flex gap-2">
                        <span className="badge bg-warning text-dark px-2.5 py-1.5 conflict-badge">
                            <strong>{conflicts.filter(c => c.fields.some(f => f.isConflict)).length}</strong> Discrepancies
                        </span>
                        <span className="badge bg-success text-white px-2.5 py-1.5 conflict-badge">
                            <strong>{conflicts.length - conflicts.filter(c => c.fields.some(f => f.isConflict)).length}</strong> Ready to Pull
                        </span>
                    </div>
                </div>

                <div className="scroll-container">
                    {!showAllSyncContacts && conflicts.filter(c => c.fields.some(f => f.isConflict)).length === 0 ? (
                        <div className="text-center p-5 bg-light rounded border" data-cy="sync-contacts-no-conflicts">
                            <i className="fa fa-check-circle text-success mb-3 success-icon"></i>
                            <h5 className="text-dark">No Discrepancies Found</h5>
                            <p className="text-muted mb-0">
                                All selected records are ready to be synced directly. Click <strong>Resolve & Pull Sync</strong> below to proceed. (Or turn on "Show all contacts" above to review).
                            </p>
                        </div>
                    ) : (
                        <Accordion>
                            {[...conflicts].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((contact, index) => {
                                const hasActualConflict = contact.fields.some(f => f.isConflict);
                                if (!hasActualConflict && !showAllSyncContacts) return null;
                                
                                return (
                                    <Accordion.Item eventKey={String(index)} key={contact.externalId} className="mb-2" data-cy={`sync-contacts-conflict-item-${contact.externalId}`}>
                                        <Accordion.Header>
                                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                                <strong className="text-dark">{contact.name}</strong>
                                                <div className="d-flex align-items-center gap-2">
                                                    {contact.isTypeSelectable ? (
                                                        <Form.Select
                                                            size="sm"
                                                            style={{ width: '150px', height: '28px', fontSize: '0.8rem', padding: '2px 8px' }}
                                                            className={!resolutions[contact.externalId]?.vendor_type ? "is-invalid border-danger text-danger" : "border-success text-success"}
                                                            value={resolutions[contact.externalId]?.vendor_type || ""}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setResolutions(prev => ({
                                                                    ...prev,
                                                                    [contact.externalId]: {
                                                                        ...prev[contact.externalId],
                                                                        vendor_type: val
                                                                    }
                                                                }));
                                                            }}
                                                        >
                                                            <option value="" disabled>-- Select Type --</option>
                                                            <option value="supplier">Supplier</option>
                                                            <option value="contractor">Contractor</option>
                                                        </Form.Select>
                                                    ) : (
                                                        <span className={`badge ${contact.detectedType === 'contractor' ? 'bg-info' : 'bg-secondary'}`}>
                                                            {contact.detectedType === 'contractor' ? 'Contractor' : 'Supplier'}
                                                        </span>
                                                    )}
                                                    {hasActualConflict ? (
                                                        <span className="badge bg-warning text-dark">Discrepancy</span>
                                                    ) : (
                                                        <span className="badge bg-success">Ready to Pull</span>
                                                    )}
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="p-3">
                                            <table className="table table-sm table-bordered mb-0">
                                                <thead>
                                                    <tr className="bg-light text-muted">
                                                        <th>Field</th>
                                                        <th>Contractable (Local)</th>
                                                        <th>External Platform</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {contact.fields.map(f => {
                                                        const isResolvingLocal = resolutions[contact.externalId]?.[f.name] === 'local';
                                                        const isResolvingExternal = resolutions[contact.externalId]?.[f.name] === 'external';
                                                        const isEmptyExternalAndMandatory = !f.external && f.isMandatory;

                                                        return (
                                                            <tr key={f.name} className={f.isConflict ? "table-warning" : ""}>
                                                                <td className="align-middle fw-semibold">{f.label}</td>
                                                                <td 
                                                                    className="align-middle cursor-pointer"
                                                                    style={{ cursor: f.isConflict ? 'pointer' : 'default' }}
                                                                    onClick={() => {
                                                                        if (f.isConflict) {
                                                                            setResolutions(prev => ({
                                                                                ...prev,
                                                                                [contact.externalId]: {
                                                                                    ...prev[contact.externalId],
                                                                                    [f.name]: 'local'
                                                                                }
                                                                            }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {f.isConflict && (
                                                                            <Form.Check
                                                                                type="radio"
                                                                                id={`res-local-${contact.externalId}-${f.name}`}
                                                                                name={`resolution-${contact.externalId}-${f.name}`}
                                                                                checked={isResolvingLocal}
                                                                                data-cy={`res-local-${contact.externalId}-${f.name}`}
                                                                                onChange={() => {}}
                                                                            />
                                                                        )}
                                                                        <span>{f.local || <em className="text-muted">[EMPTY]</em>}</span>
                                                                    </div>
                                                                </td>
                                                                <td 
                                                                    className="align-middle cursor-pointer"
                                                                    style={{ 
                                                                        cursor: (f.isConflict && !isEmptyExternalAndMandatory) ? 'pointer' : 'default',
                                                                        opacity: isEmptyExternalAndMandatory ? 0.5 : 1
                                                                    }}
                                                                    onClick={() => {
                                                                        if (f.isConflict && !isEmptyExternalAndMandatory) {
                                                                            setResolutions(prev => ({
                                                                                ...prev,
                                                                                [contact.externalId]: {
                                                                                    ...prev[contact.externalId],
                                                                                    [f.name]: 'external'
                                                                                }
                                                                            }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {f.isConflict && (
                                                                            <Form.Check
                                                                                type="radio"
                                                                                id={`res-ext-${contact.externalId}-${f.name}`}
                                                                                name={`resolution-${contact.externalId}-${f.name}`}
                                                                                checked={isResolvingExternal}
                                                                                disabled={isEmptyExternalAndMandatory}
                                                                                data-cy={`res-ext-${contact.externalId}-${f.name}`}
                                                                                onChange={() => {}}
                                                                            />
                                                                        )}
                                                                        <span>
                                                                            {f.external || <em className="text-danger">[EMPTY - Overwrite Blocked]</em>}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                {hasUnselectedTypes && (
                    <div className="text-danger me-auto fw-semibold">
                        <i className="fa fa-exclamation-triangle me-1"></i>
                        Please choose a contact type (Supplier or Contractor) for all uncategorized contacts before pulling.
                    </div>
                )}
                <Button variant="secondary" onClick={() => setShowConflictModal(false)} data-cy="sync-contacts-back-btn">
                    Back to Select
                </Button>
                <Button 
                    variant="primary" 
                    onClick={executePullSyncWithResolutions}
                    disabled={syncing || hasUnselectedTypes}
                    data-cy="sync-contacts-resolve-pull-btn"
                >
                    {syncing ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Pulling & Syncing...
                        </>
                    ) : (
                        'Resolve & Pull Sync'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
